/**
 * League Detail — standings and league hub (fullscreen: no tab bar / app chrome).
 * Lives on the Fixtures stack so Back pops to the scoreboard without remounting it.
 *
 * Query: league, leagueId, optional countryCode (flagcdn, e.g. gb-eng).
 */

import { useMemo } from 'react'
import { useLocalSearchParams } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useTheme } from '../../../src/theme/useTheme'
import { normalizeCountryCodeForFlagCdn } from '../../../src/worldcup/helpers/flagCdn.helper'
import { resolveLeagueCountryCodeForFlag } from '../../../src/shared/helpers/leagueCountryToFlagCode.helper'
import { getLeagueDetailStyles } from '../../../appStyles/_leagueDetail.styles'
import { LeagueDetailView } from '../../../components/league/LeagueDetailView'
import {
  FALLBACK_LEAGUE_COUNTRY_CODE,
  FALLBACK_LEAGUE_ID,
} from '../../../components/league/leagueDetail.constants'

function decodeSearchParam(raw: string | undefined): string | undefined {
  if (raw === undefined || raw === '') return undefined
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

export default function LeagueDetail() {
  const theme = useTheme()
  const styles = useMemo(() => getLeagueDetailStyles(theme), [theme])
  const { league: leagueParam, leagueId: leagueIdParam, countryCode: countryParam } =
    useLocalSearchParams<{
      league?: string
      leagueId?: string
      countryCode?: string
    }>()

  const leagueName = decodeSearchParam(leagueParam) ?? 'Premier League'
  const leagueId = decodeSearchParam(leagueIdParam) ?? FALLBACK_LEAGUE_ID

  const heroCountryCode = useMemo(() => {
    const raw = decodeSearchParam(countryParam)
    if (raw) {
      const normalized = normalizeCountryCodeForFlagCdn(raw)
      if (normalized) return normalized
      const fromCountryField = resolveLeagueCountryCodeForFlag(raw, leagueName)
      if (fromCountryField) return fromCountryField
    }
    if (leagueId === FALLBACK_LEAGUE_ID) {
      return normalizeCountryCodeForFlagCdn(FALLBACK_LEAGUE_COUNTRY_CODE)
    }
    return resolveLeagueCountryCodeForFlag(null, leagueName) ?? undefined
  }, [countryParam, leagueId, leagueName])

  return (
    <SafeAreaView style={styles.container} edges={['left', 'right', 'bottom']}>
      <LeagueDetailView
        leagueName={leagueName}
        leagueId={leagueId}
        countryCode={heroCountryCode ?? undefined}
      />
    </SafeAreaView>
  )
}
